import Papa from 'papaparse';

// ============================================================================
// STRATEGY DEFINITIONS
// ============================================================================

export const STRATEGIES = [
  // Sunsetted Nov 2026 — no longer meet ZTA standards; keep CSVs for research only.
  // { id: 'QR', name: 'QuantRanger', market: 'MNQ', instrument: 'Micro E-mini NASDAQ', file: '/data/portfolio/QR.csv', color: '#8b5cf6' },
  // { id: 'OR', name: 'OpenRanger', market: 'MNQ', instrument: 'Micro E-mini NASDAQ', file: '/data/portfolio/OR.csv', color: '#6366f1' },
  { id: 'QM', name: 'QuantMidas', market: 'MGC', instrument: 'Micro Gold (2 contracts)', file: '/data/portfolio/QM.csv', color: '#eab308' },
  { id: 'QN', name: 'QuantNitro', market: 'NG', instrument: 'Natural Gas', file: '/data/portfolio/QN.csv', color: '#10b981' },
  { id: 'QZ', name: 'QuantZion', market: 'MNQ', instrument: 'Micro E-mini NASDAQ', file: '/data/portfolio/QZ.csv', color: '#3b82f6', pyramid: true },
  { id: 'E8L', name: 'Engine8_Long', market: 'NQ', instrument: 'E-mini NASDAQ', file: '/data/portfolio/E8L.csv', color: '#f97316' },
  { id: 'E8S', name: 'Engine8_Short', market: 'NQ', instrument: 'E-mini NASDAQ', file: '/data/portfolio/E8S.csv', color: '#ef4444' },
  { id: 'IBX_MNQ', name: 'IBX_MNQ', market: 'MNQ', instrument: 'Micro E-mini NASDAQ', file: '/data/portfolio/IBX_MNQ.csv', color: '#0ea5e9', pyramid: true },
  { id: 'QV', name: 'QuantViper', market: 'MNQ', instrument: 'Micro E-mini NASDAQ (2 contracts)', file: '/data/portfolio/QV.csv', color: '#14b8a6' },
  // { id: 'OS2S', name: 'OS2_Short', market: 'NQ', instrument: 'E-mini NASDAQ', file: '/data/portfolio/OS2.csv', color: '#ef4444' },
  // { id: 'OS2L', name: 'OS2_Long', market: 'NQ', instrument: 'E-mini NASDAQ', file: '/data/portfolio/OS2L.csv', color: '#f97316' },
];

// ============================================================================
// CSV PARSING
// ============================================================================

const cleanCurrency = (x) => {
  if (x === null || x === undefined) return 0.0;
  let s = String(x).trim();
  s = s.replace(/[$,\s)]/g, '');
  if (String(x).includes('(')) {
    s = '-' + s.replace('(', '');
  }
  const val = parseFloat(s);
  return isNaN(val) ? 0.0 : val;
};

export const loadStrategyCSV = (csvText) => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const data = results.data;
          if (!data || data.length === 0) {
            resolve({ trades: [], error: 'File is empty' });
            return;
          }

          const headers = results.meta.fields || Object.keys(data[0]);
          const norm = {};
          headers.forEach(h => { norm[h.toLowerCase().trim()] = h; });

          const find = (candidates) => {
            for (const c of candidates) {
              if (norm[c]) return norm[c];
            }
            return null;
          };

          const pnlCol = find(['profit', 'pnl', 'net profit']);
          const maeCol = find(['mae', 'max adverse excursion']);
          const mfeCol = find(['mfe', 'max favorable excursion']);
          const exitCol = find(['exit time', 'exit date/time', 'close time', 'exit date']);

          if (!pnlCol || !exitCol) {
            resolve({ trades: [], error: `Missing columns. Found: ${headers.join(', ')}` });
            return;
          }

          const trades = data.map(row => {
            const exitTimeRaw = row[exitCol];
            if (!exitTimeRaw) return null;

            const exitTime = new Date(exitTimeRaw);
            if (isNaN(exitTime.getTime())) return null;

            return {
              exit_time: exitTime,
              date: exitTime.toISOString().split('T')[0],
              pnl: cleanCurrency(row[pnlCol]),
              mae: maeCol ? Math.abs(cleanCurrency(row[maeCol])) : 0,
              mfe: mfeCol ? Math.abs(cleanCurrency(row[mfeCol])) : 0,
            };
          }).filter(t => t !== null);

          trades.sort((a, b) => a.exit_time - b.exit_time);
          resolve({ trades, error: null });
        } catch (err) {
          resolve({ trades: [], error: err.message });
        }
      },
      error: (err) => resolve({ trades: [], error: err.message }),
    });
  });
};

export const fetchAndParseCSV = async (filePath, { pyramid = false } = {}) => {
  const resp = await fetch(filePath);
  const text = await resp.text();
  const result = await loadStrategyCSV(text);
  if (pyramid && result.trades.length > 0) {
    result.trades = consolidatePositions(result.trades);
  }
  return result;
};

const consolidatePositions = (trades) => {
  const groups = {};
  for (const t of trades) {
    const key = t.exit_time.getTime();
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  }

  const consolidated = [];
  for (const key of Object.keys(groups).sort((a, b) => a - b)) {
    const group = groups[key];
    if (group.length === 1) {
      consolidated.push(group[0]);
      continue;
    }
    const first = group[0];
    const addsMae = group.slice(1).reduce((s, t) => s + t.mae, 0);
    consolidated.push({
      exit_time: first.exit_time,
      date: first.date,
      pnl: group.reduce((s, t) => s + t.pnl, 0),
      mae: first.mae + addsMae * 0.5,
      mfe: group.reduce((s, t) => s + t.mfe, 0),
    });
  }

  return consolidated;
};

// ============================================================================
// PORTFOLIO MERGING
// ============================================================================

export const mergePortfolioTrades = (strategyTradesMap, selectedIds, startDate, endDate, quantities = {}) => {
  let allTrades = [];

  for (const id of selectedIds) {
    const trades = strategyTradesMap[id];
    if (!trades) continue;
    const qty = quantities[id] || 1;
    allTrades = allTrades.concat(trades.map(t => ({
      ...t,
      strategyId: id,
      pnl: t.pnl * qty,
      mae: t.mae * qty,
      mfe: t.mfe * qty,
    })));
  }

  if (startDate) {
    allTrades = allTrades.filter(t => t.date >= startDate);
  }
  if (endDate) {
    allTrades = allTrades.filter(t => t.date <= endDate);
  }

  allTrades.sort((a, b) => a.exit_time - b.exit_time);
  return allTrades;
};

// ============================================================================
// DAILY LIMITS (MoneyManager Simulation)
// ============================================================================

export const applyDailyLimits = (trades, maxLoss, maxProfit) => {
  if (!maxLoss && !maxProfit) return trades;

  const negMaxLoss = maxLoss ? -Math.abs(maxLoss) : null;

  const dayGroups = {};
  for (const t of trades) {
    if (!dayGroups[t.date]) dayGroups[t.date] = [];
    dayGroups[t.date].push(t);
  }

  const result = [];

  for (const date of Object.keys(dayGroups).sort()) {
    const dayTrades = dayGroups[date].sort((a, b) => a.exit_time - b.exit_time);
    let runningPnl = 0;
    let stopped = false;

    for (const trade of dayTrades) {
      if (stopped) break;

      // Check loss limit: would unrealized loss during this trade breach max loss?
      if (negMaxLoss !== null && (runningPnl - trade.mae) <= negMaxLoss) {
        const cappedPnl = negMaxLoss - runningPnl;
        result.push({ ...trade, pnl: cappedPnl, mae: Math.abs(cappedPnl), mfe: trade.mfe });
        stopped = true;
        continue;
      }

      // Check profit limit: would unrealized profit during this trade breach max profit?
      if (maxProfit && (runningPnl + trade.mfe) >= maxProfit) {
        const cappedPnl = maxProfit - runningPnl;
        result.push({ ...trade, pnl: cappedPnl, mfe: cappedPnl, mae: trade.mae });
        stopped = true;
        continue;
      }

      // No limit hit -- use actual trade
      result.push(trade);
      runningPnl += trade.pnl;

      // Check if closed PnL already hit a limit
      if (negMaxLoss !== null && runningPnl <= negMaxLoss) stopped = true;
      if (maxProfit && runningPnl >= maxProfit) stopped = true;
    }
  }

  return result;
};

// ============================================================================
// DAILY AGGREGATION
// ============================================================================

export const aggregateDaily = (trades) => {
  const dailyMap = {};

  for (const t of trades) {
    if (!dailyMap[t.date]) {
      dailyMap[t.date] = { date: t.date, pnl: 0, trades: 0, mae: 0, mfe: 0 };
    }
    dailyMap[t.date].pnl += t.pnl;
    dailyMap[t.date].trades += 1;
    dailyMap[t.date].mae += t.mae;
    dailyMap[t.date].mfe += t.mfe;
  }

  const days = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));
  return days;
};

// ============================================================================
// EQUITY CURVE
// ============================================================================

export const buildEquityCurve = (dailyData) => {
  let cumPnl = 0;
  return dailyData.map(d => {
    cumPnl += d.pnl;
    return { date: d.date, equity: cumPnl, dailyPnl: d.pnl };
  });
};

// ============================================================================
// BENCHMARK (BUY & HOLD COMPARISON)
// ============================================================================

// Module-level cache so we only fetch + parse the benchmark CSV once per page load.
let _benchmarkCache = null;

export const fetchBenchmarkData = async (symbol = 'SPY') => {
  if (_benchmarkCache && _benchmarkCache.symbol === symbol) {
    return _benchmarkCache.data;
  }
  const resp = await fetch(`/data/benchmarks/${symbol}.csv`);
  if (!resp.ok) throw new Error(`Failed to fetch ${symbol} benchmark`);
  const text = await resp.text();
  const lines = text.trim().split('\n');
  const data = [];
  for (let i = 1; i < lines.length; i++) {
    const [date, close] = lines[i].split(',');
    const c = parseFloat(close);
    if (!date || !isFinite(c)) continue;
    data.push({ date, close: c });
  }
  data.sort((a, b) => a.date.localeCompare(b.date));
  _benchmarkCache = { symbol, data };
  return data;
};

// Build a buy-and-hold curve indexed to the algo's date range.
//
// algoDailyData: [{ date, pnl, ... }] aggregated daily P&L from the portfolio
// benchmarkData: [{ date, close }] full benchmark history
// startingBalance: number (e.g. 30000)
//
// Returns: [{ date, equity, benchmarkEquity, equityPct, benchmarkPct, dailyPnl }]
//   - equity:           startingBalance + cumulative algo P&L on that day
//   - benchmarkEquity:  startingBalance scaled by benchmark close ratio since the algo's first day
//   - equityPct / benchmarkPct: percent return from startingBalance (0% on day one)
//   - Both dollar curves start at exactly startingBalance on the first algo trading day,
//     so the dollar gap is alpha and the percent gap is the same alpha on one axis.
//
// If the benchmark has no row for an algo date (weekend/holiday/missing),
// we forward-fill from the most recent prior benchmark close.
export const buildComparisonCurve = (algoDailyData, benchmarkData, startingBalance) => {
  if (!algoDailyData || algoDailyData.length === 0) return [];
  const sb = Math.max(0, Number(startingBalance) || 0);

  // Index benchmark by date for fast lookup; also keep sorted dates for forward-fill.
  const benchByDate = new Map();
  for (const b of benchmarkData) benchByDate.set(b.date, b.close);
  const benchDates = benchmarkData.map(b => b.date); // already sorted

  const findClose = (targetDate) => {
    // Exact match
    if (benchByDate.has(targetDate)) return benchByDate.get(targetDate);
    // Forward-fill: binary search for greatest benchmark date <= targetDate
    let lo = 0, hi = benchDates.length - 1, ans = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (benchDates[mid] <= targetDate) { ans = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    return ans >= 0 ? benchByDate.get(benchDates[ans]) : null;
  };

  // Anchor: first algo day for which we have a benchmark close.
  let anchorClose = null;
  for (const d of algoDailyData) {
    const c = findClose(d.date);
    if (c != null) { anchorClose = c; break; }
  }
  const pctFromStart = (value) => (value == null || sb <= 0 ? null : ((value - sb) / sb) * 100);

  if (anchorClose == null) {
    // No benchmark data at all in this range — fall back to algo-only curve.
    let cum = 0;
    return algoDailyData.map(d => {
      cum += d.pnl;
      const equity = sb + cum;
      return {
        date: d.date,
        equity,
        benchmarkEquity: null,
        equityPct: pctFromStart(equity),
        benchmarkPct: null,
        dailyPnl: d.pnl,
      };
    });
  }

  let cum = 0;
  return algoDailyData.map(d => {
    cum += d.pnl;
    const c = findClose(d.date);
    const equity = sb + cum;
    const benchmarkEquity = c != null ? sb * (c / anchorClose) : null;
    return {
      date: d.date,
      equity,
      benchmarkEquity,
      equityPct: pctFromStart(equity),
      benchmarkPct: pctFromStart(benchmarkEquity),
      dailyPnl: d.pnl,
    };
  });
};

// ============================================================================
// MAX DRAWDOWN
// ============================================================================

const computeMaxDrawdown = (dailyData) => {
  let peak = 0;
  let cumPnl = 0;
  let maxDD = 0;

  for (const d of dailyData) {
    cumPnl += d.pnl;
    if (cumPnl > peak) peak = cumPnl;
    const dd = peak - cumPnl;
    if (dd > maxDD) maxDD = dd;
  }

  return maxDD;
};

// ============================================================================
// MONTE CARLO MAX DRAWDOWN
// ============================================================================

/** Deterministic PRNG so MC drawdown is stable across renders (avoids feedback loops with UI state). */
function mulberry32(seed) {
  return function rand() {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seedFromDailyPnls(dailyPnls) {
  let s = 2166136261;
  for (let i = 0; i < dailyPnls.length; i++) {
    const x = dailyPnls[i];
    s ^= Number.isFinite(x) ? Math.round(x * 1e6) : i;
    s = Math.imul(s, 16777619);
  }
  return s >>> 0 || 1;
}

const monteCarloMaxDD = (dailyPnls, numSims = 5000, percentile = 0.95) => {
  if (dailyPnls.length < 2) return 0;

  const drawdowns = new Float64Array(numSims);
  const shuffled = new Float64Array(dailyPnls);
  const rand = mulberry32(seedFromDailyPnls(dailyPnls));

  for (let sim = 0; sim < numSims; sim++) {
    for (let k = 0; k < dailyPnls.length; k++) {
      shuffled[k] = dailyPnls[k];
    }
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      const tmp = shuffled[i];
      shuffled[i] = shuffled[j];
      shuffled[j] = tmp;
    }
    let peak = 0, equity = 0, maxDD = 0;
    for (let i = 0; i < shuffled.length; i++) {
      equity += shuffled[i];
      if (equity > peak) peak = equity;
      const dd = peak - equity;
      if (dd > maxDD) maxDD = dd;
    }
    drawdowns[sim] = maxDD;
  }

  drawdowns.sort();
  return drawdowns[Math.floor(numSims * percentile)];
};

// ============================================================================
// RISK RATIOS (daily returns vs starting equity)
// ============================================================================

const TRADING_DAYS_PER_YEAR = 252;

/** Per-day simple returns: pnl_t / equity at start of day (startingBalance + prior cumulative pnl). */
function dailySimpleReturns(dailyData, startingBalance) {
  const sb = Number(startingBalance);
  if (!isFinite(sb) || sb <= 0 || !dailyData || dailyData.length === 0) return [];

  const rets = [];
  let cum = 0;
  for (const d of dailyData) {
    const eqStart = sb + cum;
    if (eqStart > 1e-9) rets.push(d.pnl / eqStart);
    cum += d.pnl;
  }
  return rets;
}

function sharpeFromDailyReturns(dailyReturns) {
  const n = dailyReturns.length;
  if (n < 2) return null;
  const mean = dailyReturns.reduce((a, b) => a + b, 0) / n;
  const variance = dailyReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / n;
  const sd = Math.sqrt(variance);
  if (sd < 1e-12) return null;
  return (mean / sd) * Math.sqrt(TRADING_DAYS_PER_YEAR);
}

/** Sortino with MAR = 0 (downside deviation of daily returns). */
function sortinoFromDailyReturns(dailyReturns, mar = 0) {
  const n = dailyReturns.length;
  if (n < 2) return null;
  const mean = dailyReturns.reduce((a, b) => a + b, 0) / n;
  let downsideSqSum = 0;
  for (const r of dailyReturns) {
    const shortfall = Math.min(0, r - mar);
    downsideSqSum += shortfall * shortfall;
  }
  const downsideDev = Math.sqrt(downsideSqSum / n);
  if (downsideDev < 1e-12) return null;
  return ((mean - mar) / downsideDev) * Math.sqrt(TRADING_DAYS_PER_YEAR);
}

/** CAGR on portfolio equity (starting balance + total PnL) over calendar span of daily series. */
function cagrFromEquity(dailyData, startingBalance, totalPnl) {
  const sb = Number(startingBalance);
  if (!isFinite(sb) || sb <= 0 || !dailyData || dailyData.length < 1) return null;
  const startEq = sb;
  const endEq = sb + totalPnl;
  if (startEq <= 0 || endEq <= 0) return null;

  const t0 = new Date(dailyData[0].date + 'T12:00:00');
  const t1 = new Date(dailyData[dailyData.length - 1].date + 'T12:00:00');
  const years = (t1.getTime() - t0.getTime()) / (365.25 * 86400000);
  if (!isFinite(years) || years <= 1 / 365.25) return null;

  return (endEq / startEq) ** (1 / years) - 1;
}

// ============================================================================
// METRICS COMPUTATION
// ============================================================================

export const computeMetrics = (trades, dailyData, startingBalance = 30000, { mcSims = 5000 } = {}) => {
  if (!trades.length) return null;

  const profits = trades.map(t => t.pnl);
  const winners = profits.filter(p => p > 0);
  const losers = profits.filter(p => p < 0);

  const totalPnl = profits.reduce((a, b) => a + b, 0);
  const grossProfit = winners.reduce((a, b) => a + b, 0);
  const grossLoss = losers.reduce((a, b) => a + b, 0);

  const totalTrades = trades.length;
  const winCount = winners.length;
  const lossCount = losers.length;
  const winRate = winCount / totalTrades;

  const avgWin = winCount > 0 ? grossProfit / winCount : 0;
  const avgLoss = lossCount > 0 ? Math.abs(grossLoss / lossCount) : 0;

  // Daily stats
  const dailyPnls = dailyData.map(d => d.pnl);
  const winDays = dailyPnls.filter(p => p > 0);
  const loseDays = dailyPnls.filter(p => p < 0);
  const activeDaysCount = dailyData.length;

  // Market trading days (M-F) in the date range
  let totalDays = activeDaysCount;
  if (dailyData.length >= 2) {
    const firstDate = new Date(dailyData[0].date + 'T00:00:00');
    const lastDate = new Date(dailyData[dailyData.length - 1].date + 'T00:00:00');
    let weekdays = 0;
    const d = new Date(firstDate);
    while (d <= lastDate) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) weekdays++;
      d.setDate(d.getDate() + 1);
    }
    totalDays = weekdays;
  }
  const avgWinDay = winDays.length > 0 ? winDays.reduce((a, b) => a + b, 0) / winDays.length : 0;
  const avgLoseDay = loseDays.length > 0 ? loseDays.reduce((a, b) => a + b, 0) / loseDays.length : 0;

  // Monthly and yearly
  const monthlyMap = {};
  const yearlyMap = {};
  for (const d of dailyData) {
    const month = d.date.slice(0, 7);
    const year = d.date.slice(0, 4);
    monthlyMap[month] = (monthlyMap[month] || 0) + d.pnl;
    yearlyMap[year] = (yearlyMap[year] || 0) + d.pnl;
  }
  const months = Object.keys(monthlyMap).length;
  const years = Object.keys(yearlyMap).length;

  // Profit Factor
  const profitFactor = Math.abs(grossLoss) > 0 ? grossProfit / Math.abs(grossLoss) : Infinity;

  // Gain-to-Pain Ratio
  const sumAbsLosses = losers.reduce((a, b) => a + Math.abs(b), 0);
  const gainToPain = sumAbsLosses > 0 ? totalPnl / sumAbsLosses : Infinity;

  // Win/Loss Ratio
  const winLossRatio = avgLoss > 0 ? avgWin / avgLoss : Infinity;

  // Kelly Criterion: W - ((1-W) / R)
  const kelly = avgLoss > 0 ? winRate - ((1 - winRate) / (avgWin / avgLoss)) : 0;

  // PnL Standard Deviation
  const mean = totalPnl / totalTrades;
  const variance = profits.reduce((sum, p) => sum + (p - mean) ** 2, 0) / totalTrades;
  const pnlStdDev = Math.sqrt(variance);

  // Daily PnL Std Dev
  const dailyMean = dailyPnls.reduce((a, b) => a + b, 0) / totalDays;
  const dailyVariance = dailyPnls.reduce((sum, p) => sum + (p - dailyMean) ** 2, 0) / totalDays;
  const dailyPnlStdDev = Math.sqrt(dailyVariance);

  // Profit-only Std Dev
  const profitOnlyStdDev = winners.length > 1
    ? Math.sqrt(winners.reduce((sum, p) => sum + (p - avgWin) ** 2, 0) / winners.length)
    : 0;

  // Loss-only Std Dev
  const avgLossSigned = lossCount > 0 ? grossLoss / lossCount : 0;
  const lossOnlyStdDev = losers.length > 1
    ? Math.sqrt(losers.reduce((sum, p) => sum + (p - avgLossSigned) ** 2, 0) / losers.length)
    : 0;

  // System Quality Number: (expectancy / stddev of R-multiples) * sqrt(N)
  // R-multiple = trade PnL / average loss
  let sqn = 0;
  if (avgLoss > 0 && totalTrades > 0) {
    const rMultiples = profits.map(p => p / avgLoss);
    const rMean = rMultiples.reduce((a, b) => a + b, 0) / rMultiples.length;
    const rVariance = rMultiples.reduce((sum, r) => sum + (r - rMean) ** 2, 0) / rMultiples.length;
    const rStdDev = Math.sqrt(rVariance);
    if (rStdDev > 0) {
      sqn = (rMean / rStdDev) * Math.sqrt(totalTrades);
    }
  }

  // Monte Carlo 95% Max Drawdown. Sim count is configurable so the
  // recommendation engine can run a cheaper pass (e.g. 1000) while scoring
  // hundreds of candidates, then recompute the 3 displayed combos at full
  // fidelity. Defaults to 5000 to preserve PortfolioBuilder's behavior.
  const mcMaxDD95 = monteCarloMaxDD(dailyPnls, mcSims, 0.95);

  // Drawdowns
  const maxDrawdown = computeMaxDrawdown(dailyData);
  const dailyData2025 = dailyData.filter(d => d.date >= '2025-01-01' && d.date <= '2025-12-31');
  const maxDrawdown2025 = computeMaxDrawdown(dailyData2025);

  // Net Profit / Max Drawdown
  const profitDDRatio = maxDrawdown > 0 ? totalPnl / maxDrawdown : Infinity;

  // Largest losing day
  const largestLosingDay = loseDays.length > 0 ? Math.min(...loseDays) : 0;

  // Minimum Capital Required: (MC 95% DD * 3) + |largest losing day|
  const minCapitalRequired = (mcMaxDD95 * 3) + Math.abs(largestLosingDay);

  const dailyReturns = dailySimpleReturns(dailyData, startingBalance);
  const sharpeRatio = sharpeFromDailyReturns(dailyReturns);
  const sortinoRatio = sortinoFromDailyReturns(dailyReturns, 0);
  const cagr = cagrFromEquity(dailyData, startingBalance, totalPnl);

  return {
    totalPnl,
    grossProfit,
    grossLoss,

    avgPerTrade: totalPnl / totalTrades,
    avgPerDay: totalPnl / totalDays,
    avgPerMonth: months > 0 ? totalPnl / months : 0,
    avgPerYear: years > 0 ? totalPnl / years : 0,

    totalTrades,
    winCount,
    lossCount,
    winRate,
    winDaysCount: winDays.length,
    loseDaysCount: loseDays.length,
    totalDays,
    avgTradesPerDay: totalDays > 0 ? totalTrades / totalDays : 0,
    avgWinDay,
    avgLoseDay,

    profitFactor,
    gainToPain,
    winLossRatio,
    kelly,
    sqn,
    pnlStdDev,
    dailyPnlStdDev,
    profitOnlyStdDev,
    lossOnlyStdDev,
    profitDDRatio,

    sharpeRatio,
    sortinoRatio,
    cagr,

    maxDrawdown,
    maxDrawdown2025,
    mcMaxDD95,
    largestLosingDay,
    minCapitalRequired,
  };
};

// ============================================================================
// PROP FIRM SIMULATION
// ============================================================================
// Default config matches LucidFlex 50K funded (the primary account type we
// support). Callers can override any field via the second argument to model
// other sizes or firms with similar rule structures.
// ----------------------------------------------------------------------------

const DEFAULT_SIM_CONFIG = {
  drawdownType: 'eod',             // 'intraday' or 'eod'
  startBalance: 50000,
  trailingDD: 2000,
  staticLockTrigger: 52100,        // balance at which trailing DD locks
  staticFloor: 50100,              // permanent DD floor after lock
  safetyNet: 0,                    // Apex-style buffer; 0 = disabled (LucidFlex)
  minPayout: 500,
  minQualifyingDays: 5,
  minDailyProfit: 150,             // daily PnL to count as qualifying day
  consistencyPct: null,            // null = no consistency rule (LucidFlex funded)
  payoutProfitPct: 0.50,           // withdraw up to this share of cycle net profit
  maxPayouts: 5,                   // per PA; PA closes & cycles after this
  payoutCaps: [2000, 2000, 2000, 2000, 2000],
  processingDays: 1,               // calendar days until payout clears
  lockOnPayout: true,              // snap MLL to locked floor on payout request
};

export const runPropFirmSim = (trades, configOverride = {}) => {
  if (!trades || !trades.length) return null;

  const cfg = { ...DEFAULT_SIM_CONFIG, ...configOverride };
  const {
    drawdownType, startBalance, trailingDD, staticLockTrigger, staticFloor,
    safetyNet, minPayout, minQualifyingDays, minDailyProfit, consistencyPct,
    payoutProfitPct, maxPayouts, payoutCaps, processingDays, lockOnPayout,
  } = cfg;

  // ---- Per-PA state (resets on blow-up or successful max-payout cycle) ----
  let balance = startBalance;
  let threshold = startBalance - trailingDD;
  let peakEquity = startBalance;
  let isStatic = false;

  // Payout accounting — reset on approved payout
  let cumProfitSinceLastPayout = 0;
  let maxDayProfitSinceLastPayout = 0;
  let qualifyingDaysSinceLastPayout = 0;
  let payoutsApprovedCount = 0; // within current PA
  let pendingPayouts = [];

  // Per-PA "spawn → first cleared payout" tracking. Drives the
  // "Time to First Payout" stat that complements the steady-state cadence.
  let firstPayoutRecordedForPA = false;
  const paFirstPayoutDays = [];

  // ---- Global tallies ----
  let accountsBlown = 0;
  let accountsCompleted = 0;       // PAs that hit maxPayouts successfully
  let totalSecuredCash = 0;
  const payoutLog = [];
  const violations = { tradingDays: 0, profitableDays: 0, consistencyRule: 0, safetyNet: 0 };

  // ---- PA lifespan tracking (calendar days from PA start to terminal event) ----
  // Terminal event = blown OR max-payout completion. PAs still in progress at end
  // of trade history are not counted.
  const paLifespans = [];
  let paStartDate = null; // set on first iteration below
  const recordPALifespan = (terminalDay) => {
    if (!paStartDate) return;
    const days = Math.max(
      0,
      Math.round((new Date(terminalDay) - new Date(paStartDate)) / (1000 * 60 * 60 * 24))
    );
    paLifespans.push(days);
  };

  const resetPA = () => {
    pendingPayouts.forEach(p => {
      payoutLog.push({
        requestDate: p.request_date, clearedDate: 'N/A',
        amount: p.amount, status: 'FORFEITED',
      });
    });
    pendingPayouts = [];
    balance = startBalance;
    threshold = startBalance - trailingDD;
    peakEquity = startBalance;
    isStatic = false;
    cumProfitSinceLastPayout = 0;
    maxDayProfitSinceLastPayout = 0;
    qualifyingDaysSinceLastPayout = 0;
    payoutsApprovedCount = 0;
    firstPayoutRecordedForPA = false;
  };

  // Group trades by day so we can accurately distinguish intraday peaks
  // (use MFE/MAE) vs end-of-day close (use summed daily PnL).
  const uniqueDays = Array.from(new Set(trades.map(t => t.date))).sort();

  for (const currentDay of uniqueDays) {
    if (!paStartDate) paStartDate = currentDay;

    // ---- 1. Clear matured pending payouts ----
    // Use a rebuilt array instead of mutating during iteration so we can
    // cleanly break out of the loop when a PA completes its payout lifecycle.
    const stillPending = [];
    let paCycledThisStep = false;
    for (let idx = 0; idx < pendingPayouts.length; idx++) {
      const p = pendingPayouts[idx];
      if (paCycledThisStep) {
        // PA already reset this iteration — any remaining pending records
        // belonged to the old PA and are now moot.
        payoutLog.push({
          requestDate: p.request_date, clearedDate: 'N/A',
          amount: p.amount, status: 'FORFEITED',
        });
        continue;
      }
      if (currentDay < p.release_date) { stillPending.push(p); continue; }

      // Balance must still be above threshold AND safety net on clear day.
      if ((balance - p.amount) > threshold && balance >= safetyNet) {
        balance -= p.amount;
        totalSecuredCash += p.amount;
        payoutsApprovedCount++;
        payoutLog.push({
          requestDate: p.request_date, clearedDate: currentDay,
          amount: p.amount, status: 'PAID',
        });
        // First-payout-of-PA bookkeeping. Captures the long "cold start"
        // wait (clear safety net + 5 qualifying days + ~4d processing) that
        // gets diluted out of the steady-state cadence metric.
        if (!firstPayoutRecordedForPA && paStartDate) {
          const wait = Math.max(
            0,
            Math.round((new Date(currentDay) - new Date(paStartDate)) / (1000 * 60 * 60 * 24))
          );
          paFirstPayoutDays.push(wait);
          firstPayoutRecordedForPA = true;
        }
        // Per-payout cycle counters reset on approval
        cumProfitSinceLastPayout = 0;
        maxDayProfitSinceLastPayout = 0;
        qualifyingDaysSinceLastPayout = 0;

        // PA completed its max payouts — close & cycle.
        if (payoutsApprovedCount >= maxPayouts) {
          accountsCompleted++;
          recordPALifespan(currentDay);
          resetPA();
          paStartDate = currentDay;
          paCycledThisStep = true;
        }
      } else {
        payoutLog.push({
          requestDate: p.request_date, clearedDate: currentDay,
          amount: p.amount, status: 'DENIED',
        });
      }
    }
    if (!paCycledThisStep) {
      pendingPayouts = stillPending;
    }
    // If PA cycled, resetPA already cleared pendingPayouts.

    // ---- 2. Process trades for the day ----
    const dayTrades = trades.filter(t => t.date === currentDay);
    let daySurvived = true;
    let dayPnl = 0;

    for (const row of dayTrades) {
      // Intraday trailing: update peak on running intra-trade high (MFE).
      // EOD trailing: peak is ONLY updated at end of day from closing balance,
      // so no trailing update happens inside the per-trade loop.
      if (!isStatic && drawdownType === 'intraday') {
        const tradeHigh = balance + row.mfe;
        if (tradeHigh > peakEquity) {
          peakEquity = tradeHigh;
          threshold = Math.max(threshold, peakEquity - trailingDD);
        }
      }

      // Intraday firms: MAE can breach the floor mid-trade.
      // EOD firms (LucidFlex): only closing balance can breach.
      if (drawdownType === 'intraday') {
        const tradeLow = balance - row.mae;
        if (tradeLow < threshold) {
          accountsBlown++;
          recordPALifespan(currentDay);
          resetPA();
          paStartDate = currentDay;
          daySurvived = false;
          dayPnl = 0;
          break;
        }
      }

      balance += row.pnl;
      dayPnl += row.pnl;

      // Lock check runs intraday for intraday mode (balance can trigger
      // lock mid-day) and is re-checked at close for EOD mode below.
      if (!isStatic && drawdownType === 'intraday' && balance >= staticLockTrigger) {
        isStatic = true;
        threshold = staticFloor;
      }
    }

    if (!daySurvived) continue;

    // EOD peak update + EOD breach check happen after all trades close.
    if (drawdownType === 'eod') {
      if (!isStatic) {
        if (balance > peakEquity) {
          peakEquity = balance;
          threshold = Math.max(threshold, peakEquity - trailingDD);
        }
        if (balance >= staticLockTrigger) {
          isStatic = true;
          threshold = staticFloor;
        }
      }
      if (balance < threshold) {
        accountsBlown++;
        recordPALifespan(currentDay);
        resetPA();
        paStartDate = currentDay;
        continue;
      }
    }

    // ---- 3. Update payout tracking ----
    // Cycle profit is net (green and red days). Qualifying days are green-only.
    cumProfitSinceLastPayout += dayPnl;
    if (dayPnl > 0) {
      if (dayPnl > maxDayProfitSinceLastPayout) maxDayProfitSinceLastPayout = dayPnl;
      if (dayPnl >= minDailyProfit) qualifyingDaysSinceLastPayout++;
    }

    // ---- 4. Payout eligibility check ----
    // Only one pending request at a time.
    if (pendingPayouts.length > 0) continue;
    // Can't request past max payouts for this PA.
    if (payoutsApprovedCount >= maxPayouts) continue;

    // 4a. Enough qualifying days?
    if (qualifyingDaysSinceLastPayout < minQualifyingDays) {
      // Only log this as a "violation" if there was profit to work with
      if (dayPnl > 0) violations.tradingDays++;
      continue;
    }

    // 4b. Safety Net — Apex-style buffer. Skipped when safetyNet <= 0.
    if (safetyNet > 0 && balance < safetyNet + minPayout) {
      violations.safetyNet++;
      continue;
    }

    // 4c. Consistency — skipped when null / disabled (LucidFlex funded).
    if (
      consistencyPct != null &&
      consistencyPct < 1 &&
      cumProfitSinceLastPayout > 0 &&
      maxDayProfitSinceLastPayout >= cumProfitSinceLastPayout * consistencyPct
    ) {
      violations.consistencyRule++;
      continue;
    }

    // 4d. Compute payout amount.
    // LucidFlex: min(cycleNet * payoutProfitPct, flat cap), must stay above MLL.
    // Legacy/Apex-style: min(balance - safetyNet, escalating cap).
    const cap = payoutCaps[payoutsApprovedCount] ?? payoutCaps[payoutCaps.length - 1];
    let requestAmount;
    if (payoutProfitPct != null) {
      if (cumProfitSinceLastPayout <= 0) continue;
      requestAmount = Math.min(cumProfitSinceLastPayout * payoutProfitPct, cap);
      // Lucid snaps MLL to locked floor on payout request — size against that floor.
      const floorAfterRequest = lockOnPayout ? staticFloor : threshold;
      const maxByFloor = Math.max(0, balance - floorAfterRequest - 1);
      requestAmount = Math.min(requestAmount, maxByFloor);
    } else {
      requestAmount = Math.min(balance - safetyNet, cap);
    }
    if (requestAmount < minPayout) continue;

    // Lucid: requesting a payout snaps trailing MLL to the locked floor.
    if (lockOnPayout && !isStatic) {
      isStatic = true;
      threshold = staticFloor;
    }

    // Queue the payout with processing delay (calendar days).
    const releaseD = new Date(currentDay);
    releaseD.setDate(releaseD.getDate() + processingDays);
    const releaseDate = releaseD.toISOString().split('T')[0];
    pendingPayouts.push({
      amount: requestAmount,
      request_date: currentDay,
      release_date: releaseDate,
    });
  }

  // ---- Compute avg time between payouts (calendar days, conservative) ----
  const paidPayouts = payoutLog.filter(p => p.status === 'PAID');
  let avgTimeToPayout = 0;
  if (paidPayouts.length >= 2) {
    let totalDaysBetween = 0;
    for (let i = 1; i < paidPayouts.length; i++) {
      const prev = new Date(paidPayouts[i - 1].clearedDate);
      const curr = new Date(paidPayouts[i].clearedDate);
      totalDaysBetween += (curr - prev) / (1000 * 60 * 60 * 24);
    }
    avgTimeToPayout = totalDaysBetween / (paidPayouts.length - 1);
  } else if (paidPayouts.length === 1) {
    const start = new Date(trades[0].date);
    const first = new Date(paidPayouts[0].clearedDate);
    avgTimeToPayout = (first - start) / (1000 * 60 * 60 * 24);
  }

  const totalViolations =
    violations.tradingDays + violations.profitableDays +
    violations.consistencyRule + violations.safetyNet;

  const avgAccountLifespan = paLifespans.length
    ? Math.round(paLifespans.reduce((s, d) => s + d, 0) / paLifespans.length)
    : 0;

  // "Time to First Payout" — average calendar-day wait from PA spawn to
  // the first cleared payout, across every PA that actually produced one.
  // Complements avgTimeToPayout (steady-state cadence), which excludes
  // this cold-start latency.
  const avgTimeToFirstPayout = paFirstPayoutDays.length
    ? Math.round(paFirstPayoutDays.reduce((s, d) => s + d, 0) / paFirstPayoutDays.length)
    : 0;

  return {
    accountsBlown,
    accountsCompleted,
    totalPayouts: paidPayouts.length,
    totalSecuredCash,
    avgTimeToPayout: Math.round(avgTimeToPayout),
    avgTimeToFirstPayout,
    firstPayoutPACount: paFirstPayoutDays.length,
    avgAccountLifespan,
    paLifespanCount: paLifespans.length,
    payoutLog,
    violations,
    totalViolations,
    config: {
      drawdownType: cfg.drawdownType,
      minDailyProfit: cfg.minDailyProfit,
      maxPayouts: cfg.maxPayouts,
    },
  };
};
